import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import SearchBar from "./SearchBar";
import FilterSidebar from "./FilterSidebar";
import ProductGrid from "./ProductGrid";
import { Filter } from "lucide-react";
import { debounce } from "lodash";

const transformProductData = (products) => {
  return products.map((product) => {
    // Check if product has discounted prices
    const hasDiscount = product.discountedPrices && 
                       Object.keys(product.discountedPrices).length > 0;
    
    // Use 100ml price as default or first available size
    const priceKey = product.prices?.["30ml"] ? "30ml" : Object.keys(product.prices || {})[0];
    const basePrice = Number(product.prices?.[priceKey] || 0);
    const discountedPrice = hasDiscount 
      ? Number(product.discountedPrices?.[priceKey] || basePrice)
      : basePrice;
    
    // Calculate discount percentage if there's a discount
    const discount = basePrice > 0 && hasDiscount 
      ? Math.round(((basePrice - discountedPrice) / basePrice) * 100)
      : 0;

    return {
      id: product.id,
      image: product.images?.[0] || "https://via.placeholder.com/300",
      name: product.name,
      originalPrice: basePrice,
      discountedPrice: discountedPrice,
      discount: discount > 0 ? discount : null,
      isNewArrival: product.isNewArrival || false,
      category: product.category || "",
      description: product.description || "",
      fragranceNotes: product.fragranceNotes || [],
    };
  });
};

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
  </div>
);

export default function ShoppingPage() {
  const location = useLocation();
  
  // Product data and filter states
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(8);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  const debouncedSetSearchQuery = useMemo(
    () => debounce(setSearchQuery, 300),
    []
  );

  // Handle category selection from navigation
  useEffect(() => {
    if (location.state?.selectedCategory) {
      setSelectedCategory(location.state.selectedCategory);
      // Clear the navigation state to prevent persistence on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_BASE_URL}/list-products`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const transformedProducts = transformProductData(data.products || []);
        setProducts(transformedProducts);
      } catch (error) {
        console.error("Failed to fetch products:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch = product.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesPrice =
        (!priceRange.min || product.discountedPrice >= Number(priceRange.min)) &&
        (!priceRange.max || product.discountedPrice <= Number(priceRange.max));
      const matchesCategory =
        !selectedCategory || product.category === selectedCategory;

      return matchesSearch && matchesPrice && matchesCategory;
    });
  }, [products, searchQuery, priceRange, selectedCategory]);

  // Pagination logic
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, priceRange, selectedCategory]);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-center py-12 text-red-500">Error: {error}</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl md:text-4xl text-[#333333] text-center mb-8">
          Shop Our Collection
          {selectedCategory && (
            <span className="block text-lg font-normal text-[#D4AF37] mt-2">
              {selectedCategory} Products
            </span>
          )}
        </h1>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="w-full md:w-auto flex-1">
            <SearchBar
              searchQuery={searchQuery}
              setSearchQuery={debouncedSetSearchQuery}
            />
          </div>
          <button
            onClick={() => setIsFilterOpen(true)}
            className="md:hidden flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200 w-full md:w-auto"
            aria-label="Open filters"
            aria-expanded={isFilterOpen}
          >
            <Filter className="h-5 w-5" />
            Filter
          </button>
        </div>

        {/* Active Filters Display */}
        {(selectedCategory || priceRange.min || priceRange.max || searchQuery) && (
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="text-sm text-gray-600">Active filters:</span>
            {selectedCategory && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#D4AF37] text-white text-xs rounded-full">
                {selectedCategory}
                <button
                  onClick={() => setSelectedCategory("")}
                  className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                  aria-label={`Remove ${selectedCategory} filter`}
                >
                  ×
                </button>
              </span>
            )}
            {(priceRange.min || priceRange.max) && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#D4AF37] text-white text-xs rounded-full">
                Price: {priceRange.min || '0'} - {priceRange.max || '∞'}
                <button
                  onClick={() => setPriceRange({ min: "", max: "" })}
                  className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                  aria-label="Remove price filter"
                >
                  ×
                </button>
              </span>
            )}
            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#D4AF37] text-white text-xs rounded-full">
                Search: "{searchQuery}"
                <button
                  onClick={() => setSearchQuery("")}
                  className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                  aria-label="Clear search"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-64 flex-shrink-0">
            <FilterSidebar
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              isOpen={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
            />
          </div>

          <div className="flex-1">
            {filteredProducts.length > 0 ? (
              <>
                <div className="mb-4 text-sm text-gray-600">
                  Showing {currentProducts.length} of {filteredProducts.length} products
                </div>
                
                <ProductGrid products={currentProducts} />
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-8">
                    <nav className="flex items-center gap-1">
                      <button
                        onClick={() => paginate(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        &larr; Previous
                      </button>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                        <button
                          key={number}
                          onClick={() => paginate(number)}
                          className={`px-4 py-2 rounded-md border ${currentPage === number 
                            ? 'bg-[#D4AF37] text-white border-[#D4AF37]' 
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
                        >
                          {number}
                        </button>
                      ))}
                      
                      <button
                        onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next &rarr;
                      </button>
                    </nav>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-500 mb-4">
                  No products match your current filters.
                </div>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setPriceRange({ min: "", max: "" });
                    setSelectedCategory("");
                  }}
                  className="bg-[#D4AF37] text-white px-4 py-2 rounded-md hover:bg-[#C19B2E] transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
